import { useState, useEffect, useMemo } from 'react';
import Header from './components/Header/Header';
import MemberCard from './components/MemberCard/MemberCard';
import './ViewMembersPage.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getAllStudents, deleteStudent } from '../../api/studentsAPI';
import { getDecryptedUserId } from '../../utils/cryptoUtils';

// // Helper function to convert numeric year to string
// const getYearString = (year) => {
//   switch (year) {
//     case 4: return 'Year 4';
//     case 3: return 'Year 3';
//     case 2: return 'Year 2';
//     case 1: return 'Year 1';
//     default: return `Year ${year}`; // Fallback for other years
//   }
// };

const ViewMembersPage = () => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommittee, setSelectedCommittee] = useState('all')

  const [isCSC, setIsCSC] = useState(false);

  const fetchMembers = async () => {
    try {
      const data = await getAllStudents();

      const currentLoggedInId = await getDecryptedUserId();

      const formattedData = data.map(student => {
        const nameParts = (student.full_name || '').split(' ').filter(part => part);
        let initials = nameParts.length > 0 ? nameParts[0][0] : '';
        if (nameParts.length > 1) initials += nameParts[nameParts.length - 1][0];

        // --- THIS IS THE ROBUST MAPPING LOGIC FOR MEMBERSHIPS ---
        let mainRole = null;
        const committees = [];

        if (student.memberships && student.memberships.length > 0) {
          // SAFEGUARD: Check if m.role exists before calling .toLowerCase()
          const leadershipRole = student.memberships.find(m =>
            m.role && (
              m.role.toLowerCase().includes('president') ||
              m.role.toLowerCase().includes('director') ||
              m.role.toLowerCase().includes('head')
            )
          );

          // Safely determine the main role to display (exclude plain "Member")
          if (leadershipRole) {
            mainRole = leadershipRole.role;
          } else {
            const nonMemberRole = student.memberships.find(m =>
              m.role && m.role.toLowerCase() !== 'member'
            );
            mainRole = nonMemberRole?.role || null;
          }

          // Safely populate the committees list, excluding plain "Member" roles
          student.memberships.forEach(m => {
            if (m.name && m.role && m.role.toLowerCase() !== 'member') {
              committees.push(m.name);
            }
          });

          if (student.user_id.toString() === currentLoggedInId) {
            const hasCSCMembership = student.memberships.some(m =>
              m.name === 'CSC' || (m.role && m.role.toLowerCase().includes('csc'))
            );
            setIsCSC(hasCSCMembership);
          }
        }

        return {
          id: student.user_id,
          name: student.full_name,
          handle: `@${student.username}`,
          initials: initials.toUpperCase(),
          status: student.is_active ? 'active' : 'inactive',
          year: student.year,
          house: student.house,
          role: mainRole, // The main role we extracted
          committees: committees, // The list of committee names
        };
      });

      //console.log("Struct of formatted data: ", formattedData[2].committees);

      setMembers(formattedData);
    } catch (e) {
      setError(e.message);
      console.error("Failed to process members data:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleDelete = async (memberId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this member? This action cannot be undone.");

    if (confirmDelete) {
      try {
        await deleteStudent(memberId);
        fetchMembers(); // Refresh list
      } catch (e) {
        alert("Delete failed: " + e.message);
      }
    }
  };

  const committeeOptions = useMemo(() => {
    const setNames = new Set();
    members.forEach(m => {
      if (Array.isArray(m.committees)) {
        m.committees.forEach(c => {
          if (c) setNames.add(c);
        });
      }
    });

    return Array.from(setNames).map(name => ({
      committee_id: name, // use name as id
      name,
    }));
  }, [members]);

  const filteredMembers = useMemo(() => {
    const lower = searchQuery.toLowerCase();

    return members.filter(member => {
      // search bar match
      const matchesSearch = !lower || (
        (member.name && member.name.toLowerCase().includes(lower)) ||
        (member.house && member.house.toLowerCase().includes(lower)) ||
        (member.handle && member.handle.toLowerCase().includes(lower)) ||
        (member.role && member.role.toLowerCase().includes(lower)) ||
        (Array.isArray(member.committees) &&
          member.committees.some(c => c && c.toLowerCase().includes(lower)))
      );

      // committee dropdown match
      const matchesCommittee =
        selectedCommittee === 'all' ||
        (Array.isArray(member.committees) &&
          member.committees.includes(selectedCommittee));

      return matchesSearch && matchesCommittee;
    });
  }, [members, searchQuery, selectedCommittee]);

  // Function to handle export action
  const handleExport = () => {
    if (filteredMembers.length === 0) {
      alert("No members to export.");
      return;
    }

    const worksheetData = filteredMembers.map(member => ({
      'Name': member.name,
      'Tele Handle': member.handle,
      'Year': member.year,
      'House': member.house,
      'Role': member.role,
      'Committees': member.committees.join(', '),
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Members');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, 'members_list.xlsx');
  }


  const renderContent = () => {
    if (isLoading) return <p className="status-message">Loading members...</p>;
    if (error) return <p className="status-message error">Error fetching data: {error}</p>;
    if (filteredMembers.length === 0) return <p className="status-message">No members found.</p>;

    return (
      <div className="members-grid">
        {filteredMembers.map(member => (
          <MemberCard
            key={member.id}
            member={member}
            onChangeSuccess={fetchMembers}
            // REQUIREMENT 1: Only pass true if current user is CSC
            canEdit={isCSC}
            // REQUIREMENT 2: Passing the popup handler
            onDelete={() => handleDelete(member.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="page-container">
      <main className="page-content">
        <Header
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onExport={handleExport}
          committees={committeeOptions}
          selectedCommittee={selectedCommittee}
          setSelectedCommittee={setSelectedCommittee}
        />
        {renderContent()}
      </main>
    </div>
  );
};

export default ViewMembersPage;
